import {
  Modal,
  Box,
  SpaceBetween,
  Header,
  Button,
  Spinner,
  Alert,
  CopyToClipboard,
  CodeEditor,
  CodeEditorProps,
} from "@amzn/awsui-components-console";
import CodeView from "@amzn/awsui-code-view-components-console/code-view";
import jsonHighlight from "@amzn/awsui-code-view-components-console/highlight/json";
import {
  GetRegistrationByAccountingRegistrationIdCommand,
  Registration,
  UpdateAccountingRegistrationCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import React, { useEffect, useState, useCallback, useContext } from "react";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { InitDomain } from "src/context/domain/domainContext";

// Import all necessary CSS for the themes
import "ace-builds/css/ace.css";
import "ace-builds/css/theme/cloud_editor.css";
import "ace-builds/css/theme/cloud_editor_dark.css";

import jsonWorkerPath from "ace-builds/src-min-noconflict/worker-json";

interface AccountingRegistrationsModalProps {
  visible: boolean;
  accountingRegistrationId: string | null;
  onDismiss: () => void;
}

export function AccountingRegistrationModal({
  visible,
  accountingRegistrationId,
  onDismiss,
}: AccountingRegistrationsModalProps) {
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<string>("");
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [ace, setAce] = useState<any>(null);
  const [preferences, setPreferences] = useState<CodeEditorProps.Preferences>({
    wrapLines: false,
    theme: "cloud_editor",
  });

  const { getFacdClient, ardRegionMap, selectedArd } = useJemGen();
  const { authService } = useContext(InitDomain);

  // Check if user has edit permissions
  const canEdit = authService.ldap.includes("bart-admin");

  const getRegistrationByAccountingRegistrationId = useCallback(
    async (accountingRegistrationId: string) => {
      try {
        const getRegistrationByAccountingRegistrationIdCommand =
          new GetRegistrationByAccountingRegistrationIdCommand({
            accountingRegistrationId,
          });

        console.log(
          "Starting FACD getRegistrationByAccountingRegistrationId operation with getRegistrationByAccountingRegistrationIdCommand:",
          getRegistrationByAccountingRegistrationIdCommand,
        );

        if (!selectedArd || !selectedArd.id) {
          throw new Error("No ARD selected");
        }

        const domain = ardRegionMap.get(selectedArd.id);

        if (domain === undefined) {
          throw new Error(`Unknown region for ARD ${selectedArd.id}`);
        }

        const facdClient = await getFacdClient(domain);

        if (facdClient) {
          const response = await facdClient.send(
            getRegistrationByAccountingRegistrationIdCommand,
          );
          if (response && response.registration) {
            return response.registration;
          }
        } else {
          console.error("No FACD client found for region:", domain);
          throw new Error("No FACD client found for region");
        }
      } catch (error: unknown) {
        console.error(
          "An error occurred in getRegistrationByAccountingRegistrationId:",
          error,
        );
        throw error;
      } finally {
        console.log(
          "GetRegistrationByAccountingRegistrationId operation completed.",
        );
      }
    },
    [selectedArd, ardRegionMap, getFacdClient],
  );

  const fetchRegistration = useCallback(async () => {
    if (!accountingRegistrationId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getRegistrationByAccountingRegistrationId(
        accountingRegistrationId,
      );
      if (result) {
        setRegistration(result);
      } else {
        setError("Registration not found");
      }
    } catch (error: unknown) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to fetch registration details",
      );
    } finally {
      setLoading(false);
    }
  }, [accountingRegistrationId, getRegistrationByAccountingRegistrationId]);

  const updateAccountingRegistration = useCallback(
    async (newRegistrationJsonString: string) => {
      setIsUpdating(true);
      setUpdateError(null);
      setUpdateSuccess(false);

      try {
        // Parse and validate JSON
        let newRegistration: Registration;
        try {
          newRegistration = JSON.parse(newRegistrationJsonString);
        } catch (parseError) {
          throw new Error("Invalid JSON format");
        }

        // Ensure accountingRegistrationId hasn't changed
        if (
          registration &&
          newRegistration.accountingRegistrationId !==
            registration.accountingRegistrationId
        ) {
          throw new Error(
            "Cannot modify accountingRegistrationId field. This field is read-only.",
          );
        }

        // Get domain and client
        if (!selectedArd || !selectedArd.id) {
          throw new Error("No ARD selected");
        }

        const domain = ardRegionMap.get(selectedArd.id);
        if (domain === undefined) {
          throw new Error(`Unknown region for ARD ${selectedArd.id}`);
        }

        const facdClient = await getFacdClient(domain);
        if (!facdClient) {
          throw new Error("No FACD client found for region");
        }

        // Send update command
        const updateAccountingRegistrationCommand =
          new UpdateAccountingRegistrationCommand(newRegistration);
        console.log(
          "Starting FACD updateAccountingRegistration operation with updateAccountingRegistrationCommand:",
          updateAccountingRegistrationCommand,
        );

        await facdClient.send(updateAccountingRegistrationCommand);

        console.log(
          "UpdateAccountingRegistration operation completed successfully.",
        );

        // Success: show success message and refresh data
        setUpdateSuccess(true);
        setIsEditMode(false);
        setEditedContent("");

        // Refresh the registration data
        await fetchRegistration();
      } catch (error: unknown) {
        console.error(
          "An error occurred in updateAccountingRegistration:",
          error,
        );
        setUpdateError(
          error instanceof Error
            ? error.message
            : "Failed to update registration",
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [registration, selectedArd, ardRegionMap, getFacdClient, fetchRegistration],
  );

  useEffect(() => {
    if (visible && accountingRegistrationId && selectedArd) {
      fetchRegistration();
    } else if (!visible) {
      // Reset states when modal is closed
      setIsEditMode(false);
      setEditedContent("");
      setUpdateError(null);
      setUpdateSuccess(false);
    }
  }, [visible, accountingRegistrationId, selectedArd, fetchRegistration]);

  // Configure ace library (lazy-load)
  useEffect(() => {
    const loadAce = async () => {
      const aceModule = await import("ace-builds");
      aceModule.config.set("useStrictCSP", true);

      // Point to where webpack serves the worker files
      aceModule.config.setModuleUrl(
        "ace/mode/json_worker",
        jsonWorkerPath as any,
      );

      // Import mode and theme modules
      await import("ace-builds/src-noconflict/mode-json");
      await import("ace-builds/src-noconflict/theme-cloud_editor");
      await import("ace-builds/src-noconflict/theme-cloud_editor_dark");

      setAce(aceModule);
    };
    loadAce();
  }, []);

  return (
    <Modal
      size="large"
      visible={visible}
      onDismiss={onDismiss}
      closeAriaLabel="Close modal"
      header={
        <Header variant="h2">
          Accounting Registration Details
          {accountingRegistrationId && (
            <span
              style={{
                fontSize: "14px",
                fontWeight: "normal",
                marginLeft: "10px",
              }}
            >
              ({accountingRegistrationId})
            </span>
          )}
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {!isEditMode && registration && (
              <CopyToClipboard
                copyButtonText="Copy JSON"
                copyErrorText="Failed to copy JSON"
                copySuccessText="Registration JSON copied"
                textToCopy={JSON.stringify(registration, null, 2)}
                variant="button"
              />
            )}
            {!isEditMode && canEdit && registration && (
              <Button
                onClick={() => {
                  setIsEditMode(true);
                  setEditedContent(JSON.stringify(registration, null, 2));
                  setUpdateError(null);
                  setUpdateSuccess(false);
                }}
              >
                Edit
              </Button>
            )}
            {isEditMode && (
              <>
                <Button
                  onClick={() => {
                    setIsEditMode(false);
                    setEditedContent("");
                    setUpdateError(null);
                  }}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => updateAccountingRegistration(editedContent)}
                  variant="primary"
                  disabled={isUpdating}
                  loading={isUpdating}
                >
                  Save
                </Button>
              </>
            )}
            <Button
              onClick={onDismiss}
              variant={isEditMode ? "normal" : "primary"}
            >
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {loading && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box margin={{ top: "s" }}>Loading registration details...</Box>
          </Box>
        )}

        {error && !loading && (
          <Alert type="error" header="Error loading registration">
            {error}
          </Alert>
        )}

        {updateSuccess && (
          <Alert
            type="success"
            dismissible
            onDismiss={() => setUpdateSuccess(false)}
            header="Update successful"
          >
            Registration updated successfully
          </Alert>
        )}

        {updateError && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setUpdateError(null)}
            header="Update failed"
          >
            {updateError}
          </Alert>
        )}

        {registration && !loading && !error && (
          <>
            {isEditMode ? (
              <SpaceBetween size="m">
                <Alert type="info">
                  Note: The <strong>accountingRegistrationId</strong> field
                  cannot be modified.
                </Alert>
                <CodeEditor
                  ace={ace}
                  language="json"
                  value={editedContent}
                  preferences={preferences}
                  onPreferencesChange={(e) => setPreferences(e.detail)}
                  onChange={(e) => setEditedContent(e.detail.value)}
                  loading={isUpdating || !ace}
                  themes={{
                    dark: ["cloud_editor_dark"],
                    light: ["cloud_editor"],
                  }}
                  i18nStrings={{
                    loadingState: "Loading code editor",
                    errorState: "There was an error loading the code editor.",
                    errorStateRecovery: "Retry",
                    editorGroupAriaLabel: "Code editor",
                    statusBarGroupAriaLabel: "Status bar",
                    cursorPosition: (row, column) => `Ln ${row}, Col ${column}`,
                    errorsTab: "Errors",
                    warningsTab: "Warnings",
                    preferencesButtonAriaLabel: "Preferences",
                    paneCloseButtonAriaLabel: "Close",
                    preferencesModalHeader: "Preferences",
                    preferencesModalCancel: "Cancel",
                    preferencesModalConfirm: "Confirm",
                    preferencesModalWrapLines: "Wrap lines",
                    preferencesModalTheme: "Theme",
                    preferencesModalLightThemes: "Light themes",
                    preferencesModalDarkThemes: "Dark themes",
                  }}
                />
              </SpaceBetween>
            ) : (
              <CodeView
                content={JSON.stringify(registration, null, 2)}
                highlight={jsonHighlight}
              />
            )}
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
}
